import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Box, HStack, Select, Text } from '@chakra-ui/react'
import { CheckIcon } from '@heroicons/react/20/solid'
import type { ICellEditorParams } from 'ag-grid-community'
import ReactSelect from 'react-select'

import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'

export type FormOption = {
  label: string
  value: string
  description?: string
  isShowAll?: boolean
}

export const BooleanCellEditor = forwardRef(
  (
    { value, node, column }: ICellEditorParams,
    ref: React.Ref<{ getValue: () => boolean | null }>
  ) => {
    const initialValue = typeof value === 'boolean' ? value : null
    const [selectedValue, setSelectedValue] = useState<boolean | null>(initialValue)
    const selectedValueRef = useRef<boolean | null>(initialValue)
    const selectRef = useRef<HTMLSelectElement>(null)

    useImperativeHandle(ref, () => ({
      getValue: () => selectedValueRef.current
    }))

    useEffect(() => {
      selectRef.current?.focus()
    }, [])

    return (
      <Select
        ref={selectRef}
        size='sm'
        aria-label='Boolean value'
        value={selectedValue === null ? '' : String(selectedValue)}
        onChange={(event) => {
          const nextValue =
            event.currentTarget.value === '' ? null : event.currentTarget.value === 'true'
          selectedValueRef.current = nextValue
          setSelectedValue(nextValue)
          node.setDataValue(column, nextValue)
        }}
        onKeyDownCapture={(event) => {
          if (event.key === 'Escape') {
            selectedValueRef.current = initialValue
            setSelectedValue(initialValue)
            node.setDataValue(column, initialValue)
          }
        }}
      >
        <option value='true'>Yes</option>
        <option value='false'>No</option>
        <option value=''>Not set</option>
      </Select>
    )
  }
)
BooleanCellEditor.displayName = 'BooleanCellEditor'

const SHOW_ALL_OPTION_VALUE = '__show_all_options__'
const SEARCHABLE_SELECT_INITIAL_LIMIT = 8
const SEARCHABLE_SELECT_RESULT_LIMIT = 20
const SEARCHABLE_SELECT_MENU_MAX_HEIGHT = 264
const SEARCHABLE_SELECT_VIEWPORT_MARGIN = 16

const searchableSelectStyles = (isInvalid: boolean) => ({
  control: (base: any, state: any) => ({
    ...base,
    borderColor: isInvalid ? '#E53E3E' : state.isFocused ? '#319795' : base.borderColor,
    boxShadow: isInvalid
      ? '0 0 0 1px #E53E3E'
      : state.isFocused
      ? '0 0 0 1px #319795'
      : base.boxShadow,
    minHeight: '40px',
    ':hover': {
      borderColor: isInvalid ? '#E53E3E' : state.isFocused ? '#319795' : '#A0AEC0'
    }
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 1800
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 1800
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isFocused ? '#EDF2F7' : '#FFFFFF',
    color: '#1A202C',
    cursor: 'pointer',
    padding: '8px 12px'
  })
})

const normalizeSearchText = (value: string) => value.toLowerCase().replace(/\s+/g, '')

const matchesSearch = (option: FormOption, input: string) =>
  normalizeSearchText(`${option.label} ${option.description ?? ''}`).includes(input)

const initialSearchOptions = (options: FormOption[], description: string) => [
  ...options.slice(0, SEARCHABLE_SELECT_INITIAL_LIMIT),
  {
    label: `Show all ${options.length} options`,
    value: SHOW_ALL_OPTION_VALUE,
    description,
    isShowAll: true
  }
]

const renderSearchOptionLabel = (option: FormOption, { context }: { context: string }) =>
  context === 'value' ? (
    <Text noOfLines={1} title={option.label}>
      {option.label}
    </Text>
  ) : (
    <Box py={1} minWidth={0}>
      <Text
        fontSize='sm'
        fontWeight={option.isShowAll ? 'semibold' : 'medium'}
        color={option.isShowAll ? 'teal.700' : 'gray.900'}
        whiteSpace='normal'
        overflowWrap='anywhere'
      >
        {option.label}
      </Text>
      {option.description ? (
        <Text fontSize='xs' color='gray.500' whiteSpace='normal' overflowWrap='anywhere'>
          {option.description}
        </Text>
      ) : null}
    </Box>
  )

export const SearchableCellEditor = forwardRef(
  (
    {
      value,
      values = [],
      allowEmpty = false,
      renderBadges = false,
      placeholder = 'Search...',
      api
    }: ICellEditorParams & {
      values?: FormOption[]
      allowEmpty?: boolean
      renderBadges?: boolean
      placeholder?: string
    },
    ref: React.Ref<{ getValue: () => string }>
  ) => {
    const initialOption = values.find((option) => option.value === value) ?? null
    const selectedValueRef = useRef(String(value ?? ''))
    const editorContainerRef = useRef<HTMLDivElement>(null)
    const [selectedOption, setSelectedOption] = useState<FormOption | null>(initialOption)
    const [inputValue, setInputValue] = useState('')
    const [showAll, setShowAll] = useState(false)
    const [menuLayout, setMenuLayout] = useState<{
      placement: 'top' | 'bottom'
      maxHeight: number
    }>({ placement: 'bottom', maxHeight: SEARCHABLE_SELECT_MENU_MAX_HEIGHT })
    const trimmedInput = inputValue.trim()
    const normalizedInput = normalizeSearchText(trimmedInput)
    const isLargeOptionSet = values.length > SEARCHABLE_SELECT_INITIAL_LIMIT

    const visibleOptions = useMemo(() => {
      if (renderBadges || (!trimmedInput && (!isLargeOptionSet || showAll))) {
        return values
      }

      if (!trimmedInput) {
        return initialSearchOptions(values, 'Browse the full list.')
      }

      if (isLargeOptionSet && trimmedInput.length < 2) {
        return []
      }

      return values
        .filter((option) => matchesSearch(option, normalizedInput))
        .slice(0, SEARCHABLE_SELECT_RESULT_LIMIT)
    }, [isLargeOptionSet, normalizedInput, renderBadges, showAll, trimmedInput, values])

    useImperativeHandle(ref, () => ({
      getValue: () => selectedValueRef.current
    }))

    useLayoutEffect(() => {
      const editor = editorContainerRef.current
      if (!editor) {
        return
      }

      const updateMenuLayout = () => {
        const rect = editor.getBoundingClientRect()
        const spaceAbove = Math.max(0, rect.top - SEARCHABLE_SELECT_VIEWPORT_MARGIN)
        const spaceBelow = Math.max(
          0,
          window.innerHeight - rect.bottom - SEARCHABLE_SELECT_VIEWPORT_MARGIN
        )
        const placement = spaceBelow >= spaceAbove ? 'bottom' : 'top'

        setMenuLayout({
          placement,
          maxHeight: Math.min(
            SEARCHABLE_SELECT_MENU_MAX_HEIGHT,
            placement === 'bottom' ? spaceBelow : spaceAbove
          )
        })
      }

      updateMenuLayout()
      window.addEventListener('resize', updateMenuLayout)
      return () => window.removeEventListener('resize', updateMenuLayout)
    }, [])

    useEffect(() => {
      const stopEditingOnOutsideScrollIntent = (event: Event) => {
        if (event.target instanceof Node && editorContainerRef.current?.contains(event.target)) {
          return
        }

        api.stopEditing()
      }
      const stopEditingOnGridScroll = () => api.stopEditing()

      // Focus and menu layout can scroll the document without any user scroll intent.
      window.addEventListener('wheel', stopEditingOnOutsideScrollIntent, {
        capture: true,
        passive: true
      })
      window.addEventListener('touchmove', stopEditingOnOutsideScrollIntent, {
        capture: true,
        passive: true
      })
      api.addEventListener('bodyScroll', stopEditingOnGridScroll)
      return () => {
        window.removeEventListener('wheel', stopEditingOnOutsideScrollIntent, true)
        window.removeEventListener('touchmove', stopEditingOnOutsideScrollIntent, true)
        api.removeEventListener('bodyScroll', stopEditingOnGridScroll)
      }
    }, [api])

    const finishEditing = (option: FormOption | null) => {
      if (!option && !allowEmpty) {
        return
      }

      selectedValueRef.current = option?.value ?? ''
      setSelectedOption(option)
      window.requestAnimationFrame(() => api.stopEditing())
    }

    return (
      <Box
        ref={editorContainerRef}
        width={renderBadges ? '240px' : '360px'}
        maxWidth='calc(100vw - 32px)'
        bg='white'
      >
        <ReactSelect
          autoFocus
          value={selectedOption}
          options={visibleOptions}
          inputValue={inputValue}
          menuIsOpen
          menuPlacement={menuLayout.placement}
          maxMenuHeight={menuLayout.maxHeight}
          menuShouldScrollIntoView={false}
          closeMenuOnSelect={false}
          controlShouldRenderValue={renderBadges}
          components={
            renderBadges
              ? { IndicatorSeparator: null }
              : { DropdownIndicator: null, IndicatorSeparator: null }
          }
          isClearable={allowEmpty && !renderBadges}
          isSearchable={!renderBadges}
          placeholder={renderBadges ? 'Select...' : placeholder}
          onInputChange={(newInputValue, meta) => {
            if (meta.action === 'input-change') {
              setInputValue(newInputValue)
            }
          }}
          onChange={(selected: any) => {
            if (selected?.isShowAll) {
              setShowAll(true)
              setInputValue('')
              return
            }

            finishEditing(selected)
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Escape' && event.key !== 'Tab') {
              event.stopPropagation()
            }
          }}
          filterOption={() => true}
          formatOptionLabel={(option: FormOption, { context }: { context: string }) => {
            if (renderBadges) {
              const badge = mapContentToBadge(option.value || 'Not set', {
                dot: false,
                size: 'xs'
              })

              return context === 'value' ? (
                badge
              ) : (
                <HStack justify='space-between' width='100%'>
                  {badge}
                  {option.value === selectedValueRef.current ? (
                    <CheckIcon className='h-4 w-4 text-teal-600' />
                  ) : null}
                </HStack>
              )
            }

            return renderSearchOptionLabel(option, { context })
          }}
          noOptionsMessage={() =>
            isLargeOptionSet && trimmedInput.length === 1
              ? 'Type one more character to search.'
              : 'No matches.'
          }
          styles={{
            ...searchableSelectStyles(false),
            control: (base: any, state: any) => ({
              ...searchableSelectStyles(false).control(base, state),
              minHeight: '42px'
            }),
            menu: (base: any) => ({
              ...base,
              marginTop: '4px',
              minWidth: renderBadges ? '240px' : '360px',
              zIndex: 2
            }),
            menuList: (base: any) => ({
              ...base,
              padding: '4px'
            }),
            singleValue: (base: any) => ({
              ...base,
              maxWidth: renderBadges ? 'calc(100% - 24px)' : 'calc(100% - 8px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            })
          }}
        />
      </Box>
    )
  }
)
SearchableCellEditor.displayName = 'SearchableCellEditor'

export const badgeSelectCellEditor = (values: string[]) => ({
  cellEditor: SearchableCellEditor,
  cellEditorPopup: true,
  cellEditorPopupPosition: 'under' as const,
  cellEditorParams: {
    values: values.map((value) => ({ label: value || 'Not set', value })),
    renderBadges: true
  }
})

export const SearchableFormSelect = ({
  value,
  options,
  placeholder = 'Search...',
  isInvalid,
  onChange
}: {
  value: string
  options: FormOption[]
  placeholder?: string
  isInvalid: boolean
  onChange: (value: string) => void
}) => {
  const [inputValue, setInputValue] = useState('')
  const [showAll, setShowAll] = useState(false)
  const selectedOption = options.find((option) => option.value === value) ?? null
  const trimmedInput = inputValue.trim()
  const isLargeOptionSet = options.length > SEARCHABLE_SELECT_INITIAL_LIMIT
  const shouldSearchAll = !isLargeOptionSet || showAll || trimmedInput.length >= 2
  const visibleOptions = shouldSearchAll
    ? options
    : trimmedInput
    ? []
    : initialSearchOptions(options, 'Use this only when browsing is easier than searching.')

  return (
    <ReactSelect
      value={selectedOption}
      isClearable
      isSearchable
      menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
      menuPosition='fixed'
      options={visibleOptions}
      placeholder={placeholder}
      inputValue={inputValue}
      onInputChange={(newInputValue, meta) => {
        if (meta.action === 'input-change') {
          setInputValue(newInputValue)
        }
      }}
      onChange={(selected: any) => {
        if (selected?.isShowAll) {
          setShowAll(true)
          setInputValue('')
          return
        }

        setInputValue('')
        onChange(selected?.value ?? '')
      }}
      onMenuClose={() => setInputValue('')}
      filterOption={(candidate, rawInput) => {
        const option = candidate.data as FormOption
        if (option.isShowAll) {
          return true
        }

        const input = normalizeSearchText(rawInput)
        if (!input) {
          return true
        }

        return matchesSearch(option, input)
      }}
      formatOptionLabel={renderSearchOptionLabel}
      noOptionsMessage={() =>
        isLargeOptionSet && trimmedInput.length > 0 && trimmedInput.length < 2
          ? 'Type at least 2 characters to search.'
          : 'No matches.'
      }
      styles={searchableSelectStyles(isInvalid)}
    />
  )
}
